import { useCallback, useMemo } from 'react'

import { Button, Modal, useToast } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  ManageableUsersDocument,
  useCancelUserInviteMutation,
  useRemoveUserMutation,
  type User,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { UserRemovalGroupRestriction } from '../../containers'

export interface ConfirmRemoveUserContentProps {
  /**
   * User being removed.
   */
  user: User
  /**
   * The description of the user removal.
   */
  description: string
  /**
   * The note of the user removal.
   */
  note?: string
}

const sharedApolloMutationOptions = {
  onError: () => {
    // Error handled in the GraphQLErrorBoundary
  },
  refetchQueries: [
    {
      query: ManageableUsersDocument,
    },
  ],
  awaitRefetchQueries: true,
}

export const ConfirmRemoveUserContent: React.FC<
  ConfirmRemoveUserContentProps
> = ({ user, description, note }) => {
  const { t } = useTranslation('modals/settings/users')
  const { t: tCommon } = useTranslation('common')

  const modal = useModal()
  const toast = useToast()

  const [cancelUserInvite, cancelUserInviteVariables] =
    useCancelUserInviteMutation({
      onCompleted: () => {
        modal.close()

        toast.success(t('cancelInvite.success', { name: user.name }))
      },
      ...sharedApolloMutationOptions,
    })

  const [removeUser, removeUserVariables] = useRemoveUserMutation({
    onCompleted: ({ removeUser }) => {
      modal.close()

      toast.success(
        t(
          `removeUser.success.${removeUser.requiredApproval ? 'withApproval' : 'withoutApproval'}`,
          {
            name: user.name,
          },
        ),
      )
    },
    ...sharedApolloMutationOptions,
  })

  const handleCancel = useCallback(
    () => modal.close({ context: 'onDismiss' }),
    [modal],
  )

  const handleConfirm = useCallback(() => {
    // User without an id is a pending invite.
    if (user.id == null) {
      return void cancelUserInvite({
        variables: {
          email: user.email,
        },
      })
    }

    void removeUser({
      variables: {
        input: {
          userId: user.id,
          email: user.email,
        },
      },
    })
  }, [cancelUserInvite, removeUser, user])

  const loading = useMemo(() => {
    return removeUserVariables.loading || cancelUserInviteVariables.loading
  }, [removeUserVariables.loading, cancelUserInviteVariables.loading])

  const error = useMemo(() => {
    return removeUserVariables.error ?? cancelUserInviteVariables.error
  }, [removeUserVariables.error, cancelUserInviteVariables.error])

  const handleRetry = useCallback(() => {
    if (removeUserVariables.error) {
      removeUserVariables.reset()
    }

    if (cancelUserInviteVariables.error) {
      cancelUserInviteVariables.reset()
    }
  }, [cancelUserInviteVariables, removeUserVariables])

  return (
    <UserRemovalGroupRestriction user={user}>
      <GraphQLErrorBoundary error={error} retry={handleRetry} variant="page">
        <p className="mt-2 text-sm leading-6 text-black-600">{description}</p>
        {note && (
          <p className="mt-1 text-sm leading-6 text-black-600">{note}</p>
        )}
        <Modal.Footer className="mt-6" variant="stretch" dense>
          <Button disabled={loading} onClick={handleCancel} variant="secondary">
            {tCommon('cancel')}
          </Button>
          <Button loading={loading} onClick={handleConfirm} variant="primary">
            {tCommon('confirm')}
          </Button>
        </Modal.Footer>
      </GraphQLErrorBoundary>
    </UserRemovalGroupRestriction>
  )
}
